import { Component, lazy, type ReactNode, Suspense } from "react";

import { useClerkSessionHint } from "@/lib/platform/clerkSessionHint";

// Lazy because the public routes compile into the web entry chunk: a static import would put
// Clerk in the bundle every shop page and QR scan downloads (ADR-0141).
const StorefrontFollowGate = lazy(() => import("@/components/follows/StorefrontFollowGate"));

/** Renders nothing once anything below it throws, so a broken follow control never breaks a shop. */
export class SilentBoundary extends Component<{ children: ReactNode; }, { failed: boolean; }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Where a shop's page offers Follow, and the only thing the page imports to do it.
 *
 * Holds no Clerk import: the control behind it mounts its own session scope, and shows nothing
 * until a usable session exists. On web the slot renders nothing at all while the session hint
 * says signed out, so those visitors never download the control or Clerk. A chunk that fails to
 * load is as silent as one that throws, which is why the boundary wraps the Suspense.
 *
 * `onShown` carries the one fact the page may act on, that the button is on screen, so the phone's
 * name row can stay pinned exactly while Follow is offered. It reports `false` for everything else
 * and when the control goes, and a chunk that never loads never reports `true`.
 */
export function StorefrontFollowSlot(
  { slug, onFailure, onShown }: {
    slug: string;
    onFailure?: (failure: string | null) => void;
    /** Whether the Follow button is on screen: `false` for the placeholder and for nothing at all. */
    onShown?: (shown: boolean) => void;
  },
) {
  if (!useClerkSessionHint()) return null;
  return (
    <SilentBoundary>
      <Suspense fallback={null}>
        <StorefrontFollowGate slug={slug} onFailure={onFailure} onShown={onShown} />
      </Suspense>
    </SilentBoundary>
  );
}
